import { getCookie, CookieEnum } from "@/shared/utils/cookie";
import { userVar } from "./reactive-variable";
import { ensureCustomerUserPrefix } from "./loginHelpers";

/**
 * Retrieves the CLIENT_CENTRAL_TOKEN from cookies first, then localStorage
 * @returns The token string if found, null otherwise
 */
export const getClientCentralToken = (): string | null => {
  // First check cookies
  const cookieToken = getCookie(CookieEnum.CLIENT_CENTRAL_TOKEN);
  if (cookieToken) {
    return cookieToken;
  }

  // If not in cookies, check localStorage
  if (typeof window !== "undefined") {
    const localStorageToken = localStorage.getItem(
      CookieEnum.CLIENT_CENTRAL_TOKEN
    );
    if (localStorageToken) {
      return localStorageToken;
    }
  }

  return null;
};

/**
 * Checks if user has a valid token before making checkSession request
 * @returns boolean indicating if token exists
 */
export const hasValidToken = (): boolean => {
  return getClientCentralToken() !== null;
};

/**
 * Decodes a JWT token to extract payload data
 * @param token - JWT token string
 * @returns Decoded token payload or null if invalid
 */
export const decodeToken = (token: string) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/"); // JWT uses base64url encoding
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

/**
 * Validates existing token and updates user context without backend call
 * @returns boolean indicating if token was valid and user updated
 */
export const validateAndUpdateUserFromToken = (): boolean => {
  const token = getClientCentralToken();
  if (!token) {
    return false;
  }

  try {
    // Decode token locally instead of backend call
    const decodedToken = decodeToken(token);
    if (!decodedToken) {
      // Token is invalid, remove it
      localStorage.removeItem(CookieEnum.CLIENT_CENTRAL_TOKEN);
      return false;
    }

    // Check if token is expired
    const currentTime = Math.floor(Date.now() / 1000);
    if (decodedToken.exp && decodedToken.exp < currentTime) {
      // Token is expired, remove it
      localStorage.removeItem(CookieEnum.CLIENT_CENTRAL_TOKEN);
      return false;
    }

    // Update user context with decoded token data
    const userData = {
      id: ensureCustomerUserPrefix(decodedToken._id || decodedToken.id),
      name: decodedToken.name,
      email: decodedToken.email,
      phone: decodedToken.phone,
      isEmailVerfied: decodedToken.emailVerified ?? false,
      isPhoneVerified: decodedToken.phoneVerified ?? false,
      token: token,
    };

    userVar(userData);
    return true;
  } catch (error) {
    // Token is invalid, remove it
    localStorage.removeItem(CookieEnum.CLIENT_CENTRAL_TOKEN);
    return false;
  }
};

/**
 * Determines if checkSession request should be made
 * Only makes request if no token is found in cookies or localStorage
 * @returns boolean indicating if checkSession should be called
 */
export const shouldCheckSession = (): boolean => {
  return !hasValidToken();
};
